import React from 'react'
import { Button, Checkbox, Table, Tooltip } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import moment from 'moment'
import { adminEmployeesApi } from 'api/adminEmployees'
import {
  ADMIN_EMPLOYEE_ROLE_LABELS,
  EAdminEmployeeRole,
  IAdminEmployee,
  IAdminEmployeeMe
} from 'models/adminEmployees'
import { ESellerEmployeeStatus } from 'models/sellerEmployees'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { showServerError } from 'shared/utils/showServerError'
import { MainTitle } from 'components/ui'
import { EmployeeStatusTag } from 'components/employees/EmployeeStatusTag/EmployeeStatusTag'
import { AdminEmployeeModal } from './AdminEmployeeModal'
import styles from './Employees.module.scss'

const formatDate = (date: string | null) =>
  date ? moment(date).format('DD.MM.YYYY') : '—'

const getFullName = ({ name, surname }: IAdminEmployee) =>
  `${name} ${surname}`.trim()

export const ActionsEmployees: React.FC = () => {
  const isMobile = useIsMobile()
  const [me, setMe] = React.useState<IAdminEmployeeMe | null>(null)
  const [employees, setEmployees] = React.useState<IAdminEmployee[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [showFired, setShowFired] = React.useState(false)
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [editedEmployee, setEditedEmployee] =
    React.useState<IAdminEmployee | null>(null)
  // после передачи роли супер-админа роли меняются у обоих, список и права грузим заново
  const [reloadKey, setReloadKey] = React.useState(0)
  const isOwner = me?.role === EAdminEmployeeRole.OWNER

  React.useEffect(() => {
    adminEmployeesApi
      .getMe()
      .then(({ data }) => setMe(data))
      .catch(showServerError)
  }, [reloadKey])

  React.useEffect(() => {
    const getEmployees = async () => {
      try {
        setIsLoading(true)
        const { data } = await adminEmployeesApi.getEmployees(showFired)
        setEmployees(data)
      } catch (e) {
        showServerError(e)
      } finally {
        setIsLoading(false)
      }
    }
    getEmployees()
  }, [showFired, reloadKey])

  const openModal = (employee: IAdminEmployee | null) => {
    setEditedEmployee(employee)
    setIsModalOpen(true)
  }

  const onSaved = (saved: IAdminEmployee) => {
    setEmployees((prev) => {
      if (prev.some((item) => item._id === saved._id)) {
        return prev.map((item) => (item._id === saved._id ? saved : item))
      }
      // супер-админ всегда первой строкой, новые приглашения сразу под ним
      const [first, ...rest] = prev
      return first?.role === EAdminEmployeeRole.OWNER
        ? [first, saved, ...rest]
        : [saved, ...prev]
    })
    setIsModalOpen(false)
  }

  const onContactsChanged = (changed: IAdminEmployee) =>
    setEmployees((prev) =>
      prev.map((item) => (item._id === changed._id ? changed : item))
    )

  const onFired = (fired: IAdminEmployee) => {
    setEmployees((prev) =>
      showFired
        ? prev.map((item) => (item._id === fired._id ? fired : item))
        : prev.filter((item) => item._id !== fired._id)
    )
    setIsModalOpen(false)
  }

  const onRevoked = (id: string) => {
    setEmployees((prev) => prev.filter((item) => item._id !== id))
    setIsModalOpen(false)
  }

  const onOwnershipTransferred = () => {
    setIsModalOpen(false)
    setReloadKey((key) => key + 1)
  }

  const renderName = (record: IAdminEmployee) => (
    <>
      {getFullName(record) || record.email}
      {me?.employee?._id === record._id && (
        <span className={styles.youMark}> (вы)</span>
      )}
    </>
  )

  const renderRole = ({ role }: IAdminEmployee) =>
    ADMIN_EMPLOYEE_ROLE_LABELS[role]

  const columns: ColumnsType<IAdminEmployee> = [
    {
      title: 'Администратор',
      dataIndex: 'name',
      render: (_, record) => (
        <div className={styles.employeeCell}>
          <span>{renderName(record)}</span>
          {getFullName(record) && (
            <span className={styles.email}>{record.email}</span>
          )}
        </div>
      )
    },
    {
      title: 'Добавлен',
      dataIndex: 'createdAt',
      width: 112,
      render: (_, { createdAt }) => formatDate(createdAt)
    },
    {
      title: 'Роль',
      dataIndex: 'role',
      width: 140,
      render: (_, record) => renderRole(record)
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      width: 132,
      render: (_, record) => <EmployeeStatusTag {...record} />
    }
  ]

  const mobileColumns: ColumnsType<IAdminEmployee> = [
    {
      title: 'Администратор',
      dataIndex: 'name',
      width: '65%',
      render: (_, record) => (
        <div className={styles.mobileCell}>
          <span className={styles.mobileName}>{renderName(record)}</span>
          {getFullName(record) && (
            <span className={styles.mobileSubText}>{record.email}</span>
          )}
        </div>
      )
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      width: '35%',
      render: (_, record) => (
        <div className={styles.mobileStatusCell}>
          <span className={styles.mobileSubText}>{renderRole(record)}</span>
          <EmployeeStatusTag {...record} />
        </div>
      )
    }
  ]

  return (
    <div>
      <div className={styles.header}>
        <MainTitle
          text='Администраторы'
          style={{ marginBottom: 0 }}
          hideOnMobile
        />
        <div className={styles.headerControls}>
          <Checkbox
            checked={showFired}
            onChange={(e) => setShowFired(e.target.checked)}
          >
            Показать уволенных
          </Checkbox>
          {isOwner ? (
            <Button
              type='primary'
              size='large'
              icon={<PlusOutlined />}
              onClick={() => openModal(null)}
            >
              Пригласить
            </Button>
          ) : (
            me && (
              <span className={styles.ownerOnlyHint}>
                Только просмотр
                <Tooltip title='Приглашать и увольнять администраторов, а также передавать свою роль может только супер-админ'>
                  <QuestionCircleOutlined className={styles.helpIcon} />
                </Tooltip>
              </span>
            )
          )}
        </div>
      </div>
      <Table
        scroll={isMobile ? undefined : { x: 640 }}
        size={isMobile ? 'small' : undefined}
        tableLayout={isMobile ? 'fixed' : undefined}
        columns={isMobile ? mobileColumns : columns}
        dataSource={employees}
        loading={isLoading}
        rowKey={(el) => el._id}
        rowClassName={(record) =>
          [
            record.status === ESellerEmployeeStatus.FIRED && styles.firedRow,
            me && 'tableRow'
          ]
            .filter(Boolean)
            .join(' ')
        }
        onRow={(record) => ({
          onClick: () => me && openModal(record)
        })}
        pagination={false}
        locale={{ emptyText: 'Администраторов пока нет' }}
      />
      <AdminEmployeeModal
        canManage={isOwner}
        open={isModalOpen}
        employee={editedEmployee}
        onClose={() => setIsModalOpen(false)}
        onSaved={onSaved}
        onContactsChanged={onContactsChanged}
        onFired={onFired}
        onRevoked={onRevoked}
        onOwnershipTransferred={onOwnershipTransferred}
      />
    </div>
  )
}
